import { useCallback } from 'react'

import { GuestType } from '../../search-result-listing/header-section/types'
import { MAX_ADULTS, MAX_CHILDREN, MAX_PETS, MAX_TOTAL_GUESTS, MIN_ADULTS, MIN_CHILDREN, MIN_PETS } from './constants'
import { GuestCountersProps } from './types'

export const useGuestCounters = ({
    adultsCount,
    childrenCount,
    petsCount,
    onAdultsCountChange,
    onChildrenCountChange,
    onPetsCountChange,
    minAdults = MIN_ADULTS,
    maxAdults = MAX_ADULTS,
    minChildren = MIN_CHILDREN,
    maxChildren = MAX_CHILDREN,
    minPets = MIN_PETS,
    maxPets = MAX_PETS,
    maxTotalGuests = MAX_TOTAL_GUESTS,
    petsAllowed,
}: GuestCountersProps) => {
    const isAdultsIncrementCounterActive = adultsCount < maxAdults && adultsCount + childrenCount < maxTotalGuests
    const isAdultsDecrementCounterActive = adultsCount > minAdults
    const isChildrenIncrementCounterActive = childrenCount < maxChildren && adultsCount + childrenCount < maxTotalGuests
    const isChildrenDecrementCounterActive = childrenCount > minChildren
    const isPetsIncrementCounterActive = petsAllowed && (petsCount ?? 0) < maxPets
    const isPetsDecrementCounterActive = petsAllowed && (petsCount ?? 0) > minPets

    const _incrementCounter = useCallback(
        (type: GuestType) => {
            switch (type) {
                case GuestType.GUEST_TYPE_ADULT:
                    onAdultsCountChange(adultsCount < maxAdults ? adultsCount + 1 : adultsCount)
                    break
                case GuestType.GUEST_TYPE_CHILDREN:
                    onChildrenCountChange(childrenCount < maxChildren ? childrenCount + 1 : childrenCount)
                    break
                case GuestType.GUEST_TYPE_PETS:
                    if (petsAllowed && petsCount !== undefined && petsCount !== null) {
                        onPetsCountChange?.(petsCount < maxPets ? petsCount + 1 : petsCount)
                    }
                    break
                default:
                // unknown type
            }
        },
        [
            adultsCount,
            childrenCount,
            petsCount,
            maxAdults,
            maxChildren,
            maxPets,
            onAdultsCountChange,
            onChildrenCountChange,
            onPetsCountChange,
        ],
    )

    const _decrementCounter = useCallback(
        (type: GuestType) => {
            switch (type) {
                case GuestType.GUEST_TYPE_ADULT:
                    onAdultsCountChange(adultsCount > minAdults ? adultsCount - 1 : adultsCount)
                    break
                case GuestType.GUEST_TYPE_CHILDREN:
                    onChildrenCountChange(childrenCount > minChildren ? childrenCount - 1 : childrenCount)
                    break
                case GuestType.GUEST_TYPE_PETS:
                    if (petsAllowed && petsCount !== undefined && petsCount !== null) {
                        onPetsCountChange?.(petsCount > minPets ? petsCount - 1 : petsCount)
                    }
                    break
                default:
                // unknown type
            }
        },
        [
            adultsCount,
            childrenCount,
            petsCount,
            minAdults,
            minChildren,
            minPets,
            onAdultsCountChange,
            onChildrenCountChange,
            onPetsCountChange,
        ],
    )

    return {
        incrementCounter: _incrementCounter,
        decrementCounter: _decrementCounter,
        isAdultsIncrementCounterActive,
        isAdultsDecrementCounterActive,
        isChildrenIncrementCounterActive,
        isChildrenDecrementCounterActive,
        isPetsIncrementCounterActive,
        isPetsDecrementCounterActive,
    }
}
